import { SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import { Chip, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import CustomDropdown from "components/CustomDropdown/CustomDropdown";
import TicketDrawer from "components/TicketDetail/TicketDrawer";
import { apiUrl } from "features/configure";
import { useConnectWallet } from "features/home/redux/connectWallet";
import _, { get, map } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import Chat from "./Chat";
import Channel from "../components/Channel";
import Comment from "../components/Comment";
import { useFetchUserInfo } from "../redux/fetchUserInfo";
import { useFetchTeamMember, useFetchTicketList } from "../redux/hooks";
import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import {
  StyledChatAreaContainer,
  StyledMessageInfoContainer,
  StyledTicketDetailContainer,
} from "./TicketDetail.styles";
import { alertServerError, parseCustomDomainUrl } from "features/helpers/utils";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useGetPagePermission from "hooks/useGetPagePermission";
import { useIntl } from "react-intl";
import TicketTag from "../components/TicketTag";
import TicketTypeLabel from "../components/TicketTypeLabel";
import { parseToOptionFormat } from "../settings/Macros/CreateMacroModal/dropdown.utils";

import Collaborators from "../components/Collaborators";
import Prompt from "features/common/Prompt";
import { StyledSkeleton } from "components/LoadingComponent/LoadingComponent.styles";
import Switch from "@metacrm/metacrm-material-ui/dist/Switch";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { selectServiceModule } from "../redux/entityConfig/entityConfig.selector";
import {
  selectAiSupportEnabled,
  selectAiSupportConnected,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";

const useStyles = makeStyles((theme) => {
  return {
    inboxDescriptionLabel: {
      textAlign: "left",
      fontWeight: 400,
      fontSize: "12px",
      color: "#777",
      marginBottom: 8,
    },
    avatar: {
      width: 22,
      height: 22,
      marginRight: 4,
      borderRadius: 40,
    },
    selected: {
      "&.Mui-selected": {
        backgroundColor: `#F7F1FE`,
        "&:hover": {
          backgroundColor: `#F7F1FE`,
        },
      },
    },
    field: {
      marginBottom: 12,
      overflowWrap: "break-word",
    },
  };
});

const TicketDetail = ({ ticketId, onClose }) => {
  const dispatch = useDispatch();
  const [entityName, isCustomDomain] = useOutletContext();
  const [isLoading, setIsLoading] = useState({
    ticketInfo: true,
    userTicketList: true,
  });
  const [message, setMessage] = useState("");
  const { fetchTicketList, ticketListFilter } = useFetchTicketList();
  const [comment, setComment] = useState("");
  const [selectedTicket, setSelectedTicket] = useState("");

  const { auth, userData } = useConnectWallet();
  const { memberList, memberByTicketList } = useFetchTeamMember();
  const { fetchUserInfo } = useFetchUserInfo();
  const userAddress = _.get(userData, "address", "");

  const { formatMessage } = useIntl();
  const serviceModule = useSelector(selectServiceModule);

  const [aiReply, setAiReply] = useState(true);

  const navigate = useNavigate();
  const classes = useStyles();

  const aiSupportEnabled = useSelector(selectAiSupportEnabled);
  const aiSupportConnected = useSelector(selectAiSupportConnected);

  const fetchTicketDetailById = async () => {
    if (!auth || !ticketId) return;
    try {
      setIsLoading((prev) => ({ ...prev, ticketInfo: true }));
      let ticketData = await axios.get(
        `${apiUrl}/api/ticket/${ticketId}?entityName=${entityName}`
      );
      setSelectedTicket(ticketData.data);
      fetchUserInfo({ entityName, userId: _.get(ticketData, "data.user._id") });
      setComment(_.get(ticketData, "data.comment", ""));
      setAiReply(_.get(ticketData, "data.isAiChat", true));
    } catch (err) {
      dispatch(alertServerError(err));
      navigate(parseCustomDomainUrl(isCustomDomain, entityName, "/error"));
    } finally {
      setIsLoading((prev) => ({ ...prev, ticketInfo: false }));
    }
  };
  const dispatchSnackbar = useDispatchSnackbar();

  const applyToTicket = async (value) => {
    try {
      let ticketData = await axios.post(
        `${apiUrl}/api/manage/tags/${entityName}/ticket/${ticketId}/apply`,
        {
          tagIds: value,
        }
      );
    } catch (err) {
      dispatchSnackbar({ err });
    }
  };

  const tagValue = parseToOptionFormat(get(selectedTicket, "tags", []));
  const handleChooseTag = async (newValue) => {
    setSelectedTicket({
      ...selectedTicket,
      tags: map(newValue, (o) => {
        return { _id: o.value, value: o.label };
      }),
    });

    await applyToTicket(map(newValue, "_id"));
  };

  useEffect(async () => {
    await fetchTicketDetailById();
  }, [ticketId]);

  const tagTeam = _.map(selectedTicket.tagTeam, "_id");

  const pagePermission = useGetPagePermission();
  const { canEdit, readonly } = pagePermission("tickets");
  const userCanEdit =
    canEdit || selectedTicket.isAssigned || tagTeam.includes(userAddress);

  const confirm = useConfirm();

  async function handleAiReplyToggle() {
    let result = await axios.post(apiUrl + "/api/ticket/update", {
      ticketId,
      isAiChat: false,
      assignee: null,
      entityName,
    });
    setAiReply(false);
    fetchTicketDetailById();
    fetchTicketList({
      entityName,
      filter: ticketListFilter,
    });
  }

  const renderAiAutoReply = () => {
    if (!aiSupportEnabled || !aiSupportConnected) return;
    if (!userCanEdit || selectedTicket.status == "Done") return;
    if (isLoading.ticketInfo) {
      return (
        <StyledSkeleton
          animation="wave"
          sx={{ width: "80%", height: "40px" }}
        />
      );
    }
    return (
      <Box mb="20px">
        <Box mb="8px" fontSize={12} fontWeight={400} color="#777">
          {formatMessage({ id: "global.aiSupport.autoReply.label" })}
        </Box>
        <Switch
          iosSwitch
          checked={aiReply}
          onChange={(value, e) => {
            if (!value) {
              confirm({
                title: formatMessage({ id: "global.aiSupport.takeOver.title" }),
                content: formatMessage({
                  id: "global.aiSupport.takeOver.description",
                }),
                confirmText: formatMessage({
                  id: "global.aiSupport.takeOver.confirm",
                }),
                cancelText: formatMessage({
                  id: "global.aiSupport.takeOver.cancel",
                }),
                color: "secondary",
                cancelColor: "secondary",
              })
                .then(async () => {
                  await handleAiReplyToggle();
                })
                .catch(() => {});
            } else {
              confirm({
                title: formatMessage({
                  id: "global.aiSupport.reassignError.title",
                }),
                content: formatMessage({
                  id: "global.aiSupport.reassignError.description",
                }),
                confirmText: formatMessage({
                  id: "global.aiSupport.reassignError.confirm",
                }),
                color: "secondary",
              })
                .then(() => {})
                .catch(() => {});
            }
          }}
        />
      </Box>
    );
  };

  const renderAssignee = () => {
    const assignee = _.get(selectedTicket, "team._id", "");
    if (aiReply) return;
    return (
      <div>
        <div className={classes.inboxDescriptionLabel}>
          {formatMessage({ id: "ticketDetail.ticketInfo.assignee" })}
        </div>
        <div className={classes.field}>
          {isLoading.ticketInfo ? (
            <StyledSkeleton
              animation="wave"
              sx={{ width: "80%", height: "40px" }}
            />
          ) : readonly ? (
            <Chip
              label={_.get(
                _.find(memberByTicketList, {
                  _id: assignee,
                }),
                "name",
                "N/A"
              )}
            />
          ) : (
            <CustomDropdown
              border
              data={_.map(memberByTicketList, (data) => {
                if (data.name === "AI Agent") return null;
                return {
                  label: <span>{data.name || "No Name"}</span>,
                  value: data._id,
                };
              }).filter(Boolean)}
              fullWidth
              size={SIZE.LARGE}
              placeholder={"N/A"}
              value={_.get(selectedTicket, "team._id", "")}
              onSelect={async (e) => {
                let team = _.find(memberByTicketList, {
                  _id: e.target.value,
                });
                await axios.post(`${apiUrl}/api/manage/ticket/assign`, {
                  entityName,
                  ticketId: _.get(selectedTicket, "_id", ""),
                  team: team._id,
                });
                setSelectedTicket({
                  ...selectedTicket,
                  team: team,
                });
                fetchTicketList({
                  entityName,
                  filter: ticketListFilter,
                });
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderCollaborators = () => {
    const isAssignee =
      userAddress == _.get(selectedTicket, "team.address", "N/A");
    if (memberList.length == 0 || (!isAssignee && tagTeam.length == 0)) return;
    return (
      <>
        <div className={classes.inboxDescriptionLabel}>
          {formatMessage({ id: "ticketDetail.ticketInfo.collaborators" })}
        </div>
        <div className={classes.field}>
          <Collaborators
            editable={isAssignee}
            entityName={entityName}
            ticketId={_.get(selectedTicket, "_id", "")}
            selectedMembers={_.get(selectedTicket, "tagTeam", [])}
            isLoading={isLoading.ticketInfo}
          />
        </div>
      </>
    );
  };

  const renderTicketInfo = () => {
    return (
      <Box>
        <div>
          <div className={classes.inboxDescriptionLabel}>
            {formatMessage({ id: "ticket.ticketInfo.title" })}
          </div>
          <div className={classes.field}>
            {isLoading.ticketInfo ? (
              <StyledSkeleton animation="wave" sx={{ width: "80%" }} />
            ) : (
              selectedTicket.title
            )}
          </div>
          <div className={classes.inboxDescriptionLabel}>
            {formatMessage({ id: "ticketList.filter.Issue" })}
          </div>
          <div className={classes.field}>
            {isLoading.ticketInfo ? (
              <StyledSkeleton
                animation="wave"
                sx={{ width: "80%", height: "40px" }}
              />
            ) : (
              <TicketTypeLabel
                ticketType={selectedTicket.type}
                edit={userCanEdit}
                callback={async (newValue) => {
                  await axios.post(`${apiUrl}/api/ticket/updateTicketType`, {
                    entityName,
                    ticketId: _.get(selectedTicket, "_id", ""),
                    ticketTypeId: newValue,
                  });
                  fetchTicketDetailById();
                  fetchTicketList({
                    entityName,
                    filter: ticketListFilter,
                  });
                }}
              />
            )}
          </div>
          <div className={classes.inboxDescriptionLabel}>
            {formatMessage({ id: "ticketList.filter.channel" })}:
          </div>
          <div className={classes.field}>
            {isLoading.ticketInfo ? (
              <Box className="item-flex" sx={{ gap: "5px" }}>
                <StyledSkeleton
                  animation="wave"
                  sx={{ width: "20px", height: "20px" }}
                />
                <StyledSkeleton animation="wave" sx={{ width: "40%" }} />
              </Box>
            ) : (
              <Channel ticket={selectedTicket} showLabel={true} />
            )}
          </div>
          <div className={classes.inboxDescriptionLabel}>
            {formatMessage({
              id: "ticketDetail.ticketInfo.ticketTag",
            })}
          </div>
          <div className={classes.field}>
            {isLoading.ticketInfo ? (
              <StyledSkeleton
                animation="wave"
                sx={{ width: "80%", height: "40px" }}
              />
            ) : (
              <TicketTag
                canEdit={userCanEdit}
                tags={tagValue}
                handleChooseTag={handleChooseTag}
              />
            )}
          </div>
        </div>
        <Box
          pt="20px"
          mt="20px"
          mb="25px"
          borderTop="1px solid #E6E6E6"
          color="#555"
          fontSize="12px"
        >
          {formatMessage({
            id: "ticketDetail.ticketInfo.collaboration",
          })}
        </Box>
        <div>
          {renderAiAutoReply()}
          {renderAssignee()}
          {renderCollaborators()}

          <div
            className={classes.inboxDescriptionLabel}
            style={{ marginTop: 12 }}
          >
            {formatMessage({
              id: "ticketDetail.ticketInfo.comment",
            })}
          </div>
          <Comment
            entityName={entityName}
            selectedTicket={selectedTicket}
            row={false}
            comment={comment}
            setComment={setComment}
            isLoading={isLoading.ticketInfo}
          />
        </div>
      </Box>
    );
  };
  const leaveTicket = () => {
    setSelectedTicket("");
  };

  return (
    <StyledTicketDetailContainer>
      <Grid
        container
        alignItems="stretch"
        direction="row"
        justifyContent="center"
        height="100%"
      >
        <StyledMessageInfoContainer>
          <IconButton
            size="14px"
            color="#555"
            onClick={() => {
              onClose();
              if (message) return;
              leaveTicket();
            }}
            style={{
              marginBottom: "20px",
            }}
          >
            <i className="meta-crm-icon-ic_cancel font-size-14 mgr4" />
            <Box sx={{ fontSize: "12px", fontWeight: 500, color: "#555" }}>
              {formatMessage({ id: "global.status.close" })}
            </Box>
          </IconButton>
          {renderTicketInfo()}
        </StyledMessageInfoContainer>
        <StyledChatAreaContainer>
          <Chat
            fetchTicketDetailById={fetchTicketDetailById}
            selectedTicket={selectedTicket}
            entityName={entityName}
            setSelectedTicket={setSelectedTicket}
            userCanEdit={userCanEdit}
            setMessage={setMessage}
            message={message}
          />
        </StyledChatAreaContainer>
        <TicketDrawer
          entityName={entityName}
          selectedTicket={selectedTicket}
          userCanEdit={userCanEdit}
          userAddress={userAddress}
        />
        <Prompt
          when={message}
          confirmEveryTime
          confirmCallback={() => {
            if (!ticketId) leaveTicket();
            else {
              setMessage("");
            }
          }}
        />
      </Grid>
    </StyledTicketDetailContainer>
  );
};

export default TicketDetail;
